import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Pencil, Trash2, X, ArrowLeft,
  ChevronLeft, ChevronRight, Search, UserMinus, UserCheck,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SortableHeader, { PAGE_SIZE } from '../components/SortableTable';
import ResultCount from '../components/shared/ResultCount';
import SegmentedSwitch from '../components/shared/SegmentedSwitch';
import { formatDate, formatDateShort } from '../utils/dateFormat';
import { firmToday } from '../utils/dateRange';
import {
  Field, inputCls, grid2, btnPrimary, btnGhost, modalOverlayCls,
} from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation } from '../components/shared/formValidation';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const THEAD_CLS = 'px-4 py-2 text-xs font-semibold text-slate-500 uppercase';

const fetchJSON = async (url, opts = {}) => {
  const res = await fetch(`${API_BASE_URL}${url}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.status = res.status;
    err.code = data.code;
    throw err;
  }
  return data;
};

// Dates arrive as YYYY-MM-DD or full ISO strings — keep the date part only.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

// Employment status is read from the leaving date alone: a date means left.
// The server and the staff table enforce the same rules.
// Stage keys as the reviewer-standing look-up returns them
// (services/reviewerStandingWatch.js).
const STAGE_WORD = {
  engagement: 'First reviewer', additional: 'Second reviewer', third: 'Third reviewer',
};

const leavingDateError = (dateLeft, dateJoined) => {
  if (!dateLeft) return null;
  if (dateLeft > firmToday()) return 'The leaving date cannot be in the future.';
  if (dateJoined && dateLeft < dateJoined) return 'The leaving date cannot be earlier than the joining date.';
  return null;
};

const STATUS_FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'left', label: 'Left' },
  { key: 'all', label: 'All' },
];

const EMPLOYMENT_STATUSES = [
  { key: 'active', label: 'Active' },
  { key: 'left', label: 'Left' },
];

const StatusBadge = ({ dateLeft }) => (dateLeft ? (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 whitespace-nowrap">
    Left · {formatDateShort(dateLeft)}
  </span>
) : (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700">
    Active
  </span>
));

const QMStaff = () => {
  const { hasPermission } = useAuth();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortConfig, setSortConfig] = useState(null);

  const requestSort = (key) => {
    setPage(1);
    setSortConfig(prev =>
      prev && prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' });
  };

  // Managed lists (Settings) that back the form + filter dropdowns.
  const [lookups, setLookups] = useState({ company: [], department: [], designation: [] });

  const [editing, setEditing] = useState(null);
  // {userId, from, to} after an email change that carried a login account with
  // it — worth saying out loud, since the person has to sign in again.
  const [accountNotice, setAccountNotice] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [markingLeft, setMarkingLeft] = useState(null);
  const [reactivating, setReactivating] = useState(null);

  const canEdit = hasPermission('qm:staff:edit');

  // Debounce the search box so we don't refetch on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to first page whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, companyFilter, departmentFilter, designationFilter, statusFilter]);

  // Load the three staff master lists once.
  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    const loadLookups = async () => {
      try {
        const [c, d, g] = await Promise.all([
          fetchJSON('/api/settings/lookups/staff_company?active=true'),
          fetchJSON('/api/settings/lookups/staff_department?active=true'),
          fetchJSON('/api/settings/lookups/staff_designation?active=true'),
        ]);
        if (cancelled) return;
        setLookups({
          company: (c.options || []).map(o => o.value),
          department: (d.options || []).map(o => o.value),
          designation: (g.options || []).map(o => o.value),
        });
      } catch (e) {
        console.error('Error loading staff lists:', e);
      }
    };
    loadLookups();
    return () => { cancelled = true; };
  }, [canEdit]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (companyFilter) params.set('company', companyFilter);
      if (departmentFilter) params.set('department', departmentFilter);
      if (designationFilter) params.set('designation', designationFilter);
      if (statusFilter !== 'all') params.set('active', statusFilter === 'active' ? 'true' : 'false');
      if (sortConfig?.key) { params.set('sort', sortConfig.key); params.set('dir', sortConfig.direction); }
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      const data = await fetchJSON(`/api/qm/staff?${params}`);
      setRows(data.data || []);
      setTotal(data.total || 0);
      setCounts(data.counts || null);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [debouncedSearch, companyFilter, departmentFilter, designationFilter, statusFilter, sortConfig, page]);

  useEffect(() => {
    if (!canEdit) return;
    load();
  }, [canEdit, load]);

  if (!canEdit) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need staff record editing rights to access this area.</p>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasFilters = !!(search || companyFilter || departmentFilter || designationFilter);
  const clearFilters = () => {
    setSearch('');
    setCompanyFilter('');
    setDepartmentFilter('');
    setDesignationFilter('');
  };

  const statusOptions = STATUS_FILTERS.map((s) => ({
    ...s,
    count: !counts ? null
      : s.key === 'active' ? counts.active
        : s.key === 'left' ? counts.left
          : counts.active + counts.left,
  }));

  const filterSelectCls = `${inputCls} sm:w-60`;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Staff</h1>
          <p className="text-sm text-slate-500 mt-1">Manage the staff master feeding the Timesheets, Training, Chargeable Hours and Headcount views.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> New Staff
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedSwitch
            ariaLabel="Employment status"
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email or designation"
              className={`${inputCls} pl-9`}
            />
          </div>
          <ResultCount count={total} noun="staff member" plural="staff" loading={loading} className="ml-auto" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select value={companyFilter} onChange={e => setCompanyFilter(e.target.value)} className={filterSelectCls} aria-label="Company">
            <option value="">All Companies</option>
            {lookups.company.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)} className={filterSelectCls} aria-label="Department">
            <option value="">All Departments</option>
            {lookups.department.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={designationFilter} onChange={e => setDesignationFilter(e.target.value)} className={filterSelectCls} aria-label="Designation">
            <option value="">All Designations</option>
            {lookups.designation.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">{error}</div>
      )}

      {accountNotice && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-blue-800 text-sm mb-4 flex items-start justify-between gap-3">
          <span>
            The login account <span className="font-semibold">{accountNotice.from}</span> now signs in
            as <span className="font-semibold">{accountNotice.to}</span>, with the same password and no
            new verification code.
          </span>
          <button onClick={() => setAccountNotice(null)} className="text-blue-400 hover:text-blue-600 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <SortableHeader label="Name" sortKey="name" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                <SortableHeader label="Designation" sortKey="designation" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                <SortableHeader label="Department" sortKey="department" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                <SortableHeader label="Company" sortKey="company" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                <SortableHeader label="Joined" sortKey="dateJoined" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                <SortableHeader label="Actions" sortable={false} align="right" className={THEAD_CLS} />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">No staff match these filters.</td>
                </tr>
              ) : rows.map(r => {
                const left = !!r.dateLeft;
                const cellText = left ? 'text-slate-400' : 'text-slate-700';
                return (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <div className={`font-medium ${left ? 'text-slate-500' : 'text-slate-900'}`}>{r.name}</div>
                      <div className="text-xs text-slate-400">{r.email || '—'}</div>
                    </td>
                    <td className={`px-4 py-2 ${cellText}`}>{r.designation || '—'}</td>
                    <td className={`px-4 py-2 ${cellText}`}>{r.department || '—'}</td>
                    <td className={`px-4 py-2 ${cellText}`}>{r.company || '—'}</td>
                    <td className={`px-4 py-2 whitespace-nowrap ${cellText}`}>{r.dateJoined ? formatDateShort(r.dateJoined) : '—'}</td>
                    <td className="px-4 py-2"><StatusBadge dateLeft={r.dateLeft} /></td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          onClick={() => setEditing(r)}
                          title="Edit"
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        {left ? (
                          <button
                            onClick={() => setReactivating(r)}
                            title="Reactivate"
                            className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded"
                          >
                            <UserCheck className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => setMarkingLeft(r)}
                            title="Mark as Left"
                            className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded"
                          >
                            <UserMinus className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => setDeleting(r)}
                          title="Delete"
                          className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm">
          <span className="text-slate-500">
            {total > 0
              ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`
              : '0 of 0'}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-slate-500">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {editing && (
        <StaffModal
          staff={editing}
          lookups={lookups}
          onClose={() => setEditing(null)}
          onSaved={(accountEmail) => { setEditing(null); setAccountNotice(accountEmail); load(); }}
        />
      )}
      {showCreate && (
        <StaffModal
          staff={null}
          lookups={lookups}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {deleting && (
        <DeleteStaffModal
          staff={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); load(); }}
        />
      )}
      {markingLeft && (
        <MarkAsLeftModal
          staff={markingLeft}
          onClose={() => setMarkingLeft(null)}
          onMarked={() => { setMarkingLeft(null); load(); }}
        />
      )}
      {reactivating && (
        <ReactivateModal
          staff={reactivating}
          onClose={() => setReactivating(null)}
          onDone={() => { setReactivating(null); load(); }}
        />
      )}
    </div>
  );
};

// Select backed by a managed list, keeping the record's current value visible
// even when it is no longer in the active list.
const LookupSelect = ({ value, options, onChange }) => (
  <select value={value} onChange={e => onChange(e.target.value)} className={inputCls}>
    <option value="">Select…</option>
    {options.map(o => <option key={o} value={o}>{o}</option>)}
    {value && !options.includes(value) && <option value={value}>{value}</option>}
  </select>
);

const ErrorBanner = ({ children }) => (
  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
    <span>{children}</span>
  </div>
);

const ModalHeader = ({ title, onClose }) => (
  <div className="flex items-center justify-between p-6 border-b border-slate-200">
    <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
      <X className="w-5 h-5" />
    </button>
  </div>
);

const StaffModal = ({ staff, lookups, onClose, onSaved }) => {
  const isEdit = !!staff;
  const today = firmToday();

  const [form, setForm] = useState({
    name: staff?.name || '',
    email: staff?.email || '',
    company: staff?.company || '',
    department: staff?.department || '',
    designation: staff?.designation || '',
    initials: staff?.initials || '',
    dateJoined: toDateInput(staff?.dateJoined),
    dateLeft: toDateInput(staff?.dateLeft),
    status: staff?.dateLeft ? 'left' : 'active',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Shown when Active is picked while a leaving date is still filled in.
  const [clearFirst, setClearFirst] = useState(false);
  // The users↔staff link and the person's workflow scope both hang off the
  // email, so editing it on an existing record needs an explicit go-ahead.
  const [confirmEmailChange, setConfirmEmailChange] = useState(false);
  // Designation and leaving date decide reviewer standing, which is re-read at
  // the signature — so this edit can leave an in-flight request nobody is able
  // to approve. The requests are shown before saving; the edit itself is never
  // blocked (a leaving date is often back-dated, and HR should not be held up
  // by a workflow). { requests } once confirmed.
  const [standingWarning, setStandingWarning] = useState(null);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const pickStatus = (status) => {
    if (status === 'active' && form.dateLeft) {
      setClearFirst(true);
      return;
    }
    setClearFirst(false);
    setField('status', status);
  };

  const setDateLeft = (v) => {
    setForm(prev => ({ ...prev, dateLeft: v, status: v ? 'left' : prev.status }));
    if (v) setClearFirst(false);
  };

  const clearDateLeft = () => {
    setField('dateLeft', '');
    setClearFirst(false);
  };

  const isLeft = isEdit && form.status === 'left';
  const dateLeftError = isLeft ? leavingDateError(form.dateLeft, form.dateJoined) : null;

  // Case-only edits change nothing that matters — matching is case-insensitive
  // on both sides — so they save without the confirmation step.
  const emailChanged = isEdit && !!(staff.email || '').trim()
    && form.email.trim().toLowerCase() !== (staff.email || '').trim().toLowerCase();

  // The two fields reviewer standing is read from.
  const nextDateLeft = form.status === 'left' ? (form.dateLeft || '') : '';
  const standingFieldsChanged = isEdit && (
    form.designation !== (staff.designation || '')
    || nextDateLeft !== toDateInput(staff.dateLeft)
  );

  // 空白的必填格：按 Save 後就地標紅並列進橫幅，填好就消。
  const gaps = useMemo(() => {
    const g = [];
    if (!form.name.trim()) g.push({ key: 'name', label: 'Name' });
    if (!form.email.trim()) g.push({ key: 'email', label: 'Email' });
    if (!form.company.trim()) g.push({ key: 'company', label: 'Company' });
    if (!form.department.trim()) g.push({ key: 'department', label: 'Department' });
    if (!form.designation.trim()) g.push({ key: 'designation', label: 'Designation' });
    if (isEdit && form.status === 'left' && !form.dateLeft) g.push({ key: 'dateLeft', label: 'Date Left' });
    return g;
  }, [form, isEdit]);
  const validation = useFormValidation(gaps);

  const save = async () => {
    if (!validation.validate()) { setError(null); return; }
    if (dateLeftError) { setError(dateLeftError); return; }

    if (emailChanged && !confirmEmailChange) {
      setConfirmEmailChange(true);
      return;
    }

    if (standingFieldsChanged && !standingWarning) {
      try {
        const open = await fetchJSON(`/api/qm/staff/${staff.id}/open-reviews`);
        if ((open.requests || []).length > 0) {
          setStandingWarning({ requests: open.requests });
          return;
        }
      } catch {
        // The look-up is information, not a gate: if it fails, the edit goes
        // through and the requester is still mailed by the server.
      }
    }

    setSaving(true);
    setError(null);
    const body = {
      name: form.name.trim(),
      email: form.email.trim(),
      company: form.company,
      department: form.department,
      designation: form.designation,
      initials: form.initials.trim() || null,
      dateJoined: form.dateJoined || null,
    };
    if (isEdit) {
      body.dateLeft = form.status === 'left' ? form.dateLeft : null;
    }
    try {
      if (isEdit) {
        // accountEmail comes back when a login account moved with the email.
        const saved = await fetchJSON(`/api/qm/staff/${staff.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        onSaved(saved.accountEmail || null);
        return;
      }
      await fetchJSON('/api/qm/staff', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      onSaved(null);
    } catch (e) {
      // Back to the form so the message is readable there.
      setConfirmEmailChange(false);
      if (e.code === 'DUPLICATE_STAFF') {
        setError('A staff member with this email already exists.');
      } else {
        setError(e.message);
      }
    }
    finally { setSaving(false); }
  };

  const bannerText = validation.error || error;

  return (
    <div className={modalOverlayCls}>
      <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <ModalHeader title={isEdit ? `Edit ${staff.name}` : 'New Staff'} onClose={onClose} />
        <div className="p-6 space-y-4">
          <FormValidationProvider value={validation.providerValue}>
          <Field label="Name" required field="name">
            <input type="text" value={form.name} onChange={e => setField('name', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Email" required field="email">
            <input type="email" value={form.email} onChange={e => setField('email', e.target.value)} className={inputCls} />
          </Field>
          <div className={grid2}>
            <Field label="Company" required field="company">
              <LookupSelect value={form.company} options={lookups.company} onChange={v => setField('company', v)} />
            </Field>
            <Field label="Department" required field="department">
              <LookupSelect value={form.department} options={lookups.department} onChange={v => setField('department', v)} />
            </Field>
          </div>
          <div className={grid2}>
            <Field label="Designation" required field="designation">
              <LookupSelect value={form.designation} options={lookups.designation} onChange={v => setField('designation', v)} />
            </Field>
            <Field label="Initials">
              <input type="text" value={form.initials} onChange={e => setField('initials', e.target.value)} placeholder="e.g. CWJ" className={inputCls} />
            </Field>
          </div>
          <div className={grid2}>
            <Field label="Date Joined">
              <input type="date" value={form.dateJoined} onChange={e => setField('dateJoined', e.target.value)} className={inputCls} />
            </Field>
            {isEdit && (
              <Field label="Employment Status">
                <SegmentedSwitch
                  ariaLabel="Employment status"
                  options={EMPLOYMENT_STATUSES}
                  value={form.status}
                  onChange={pickStatus}
                />
              </Field>
            )}
          </div>
          {clearFirst && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
              <span>Clear the leaving date first to set this staff member as active.</span>
            </div>
          )}
          {isLeft && (
            <Field
              label="Date Left"
              required
              field="dateLeft"
              error={dateLeftError}
              action={form.dateLeft ? (
                <button type="button" onClick={clearDateLeft} className="text-xs text-blue-600 hover:text-blue-800">
                  Clear Date
                </button>
              ) : null}
            >
              <input
                type="date"
                value={form.dateLeft}
                min={form.dateJoined || undefined}
                max={today}
                onChange={e => setDateLeft(e.target.value)}
                className={inputCls}
              />
            </Field>
          )}
          {bannerText && <ErrorBanner>{bannerText}</ErrorBanner>}
          </FormValidationProvider>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className={btnGhost}>Cancel</button>
            <button onClick={save} disabled={saving} className={btnPrimary}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      {confirmEmailChange && (
        <div className="fixed inset-0 bg-slate-900/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <ModalHeader title="Confirm Email Change" onClose={() => setConfirmEmailChange(false)} />
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-700">
                You are changing <span className="font-semibold">{staff.name}</span>&apos;s email
                from <span className="font-semibold">{staff.email}</span> to <span className="font-semibold">{form.email.trim()}</span>.
              </p>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                <span>
                  The login account signing in with the old address moves to the new one at the same
                  time — same password, no new verification code. Anyone signed in right now is asked
                  to sign in again with the new address.
                </span>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setConfirmEmailChange(false)} className={btnGhost}>Cancel</button>
                <button onClick={save} disabled={saving} className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Change Email'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {standingWarning && (
        <div className="fixed inset-0 bg-slate-900/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <ModalHeader title="Requests Awaiting This Reviewer" onClose={() => setStandingWarning(null)} />
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-700">
                <span className="font-semibold">{staff.name}</span> is the named reviewer of
                {' '}{standingWarning.requests.length}{' '}
                request{standingWarning.requests.length === 1 ? '' : 's'} still under review.
              </p>
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                {standingWarning.requests.map(r => (
                  <li key={`${r.flow}-${r.id}`} className="px-3 py-2 text-sm">
                    <span className="font-medium text-slate-800">{r.requestNo}</span>
                    <span className="text-slate-500"> · {r.label}</span>
                    <span className="block text-xs text-slate-500">
                      {STAGE_WORD[r.stage] || r.stage} · requested by {r.requesterEmail}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                <span>
                  This change is saved either way. If it leaves them without the grade a stage
                  requires, they can no longer approve, and each request&apos;s requester is emailed
                  to modify it and pick another reviewer.
                </span>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setStandingWarning(null)} className={btnGhost}>Cancel</button>
                <button onClick={save} disabled={saving} className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save Anyway'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DeleteStaffModal = ({ staff, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [hasRecords, setHasRecords] = useState(false);

  const confirmDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await fetchJSON(`/api/qm/staff/${staff.id}`, { method: 'DELETE' });
      onDeleted();
    } catch (e) {
      if (e.code === 'HAS_RECORDS') {
        setHasRecords(true);
        setError('This staff member has monthly or training records and cannot be deleted. Mark them as left instead — history stays intact.');
      } else {
        setError(e.message);
      }
    }
    finally { setDeleting(false); }
  };

  return (
    <div className={modalOverlayCls}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <ModalHeader title="Delete Staff" onClose={onClose} />
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            Are you sure you want to delete <span className="font-semibold">{staff.name}</span>? This action cannot be undone.
            Deletion is only possible when the staff member has no monthly or training records.
          </p>
          {error && <ErrorBanner>{error}</ErrorBanner>}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className={btnGhost}>
              {hasRecords ? 'Close' : 'Cancel'}
            </button>
            {!hasRecords && (
              <button onClick={confirmDelete} disabled={deleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50">
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MarkAsLeftModal = ({ staff, onClose, onMarked }) => {
  const today = firmToday();
  const [dateLeft, setDateLeft] = useState(today);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const joined = toDateInput(staff.dateJoined);
  const ruleError = leavingDateError(dateLeft, joined);
  const validation = useFormValidation(dateLeft ? [] : [{ key: 'dateLeft', label: 'Date Left' }]);

  const confirm = async () => {
    if (!validation.validate() || ruleError) return;
    setSubmitting(true);
    setError(null);
    try {
      await fetchJSON(`/api/qm/staff/${staff.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ dateLeft }),
      });
      onMarked();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className={modalOverlayCls}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <ModalHeader title="Mark as Left" onClose={onClose} />
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            Mark <span className="font-semibold">{staff.name}</span> as left?
            They will disappear from active staff dropdowns; historical records remain in the dashboards.
          </p>
          <FormValidationProvider value={validation.providerValue}>
            <Field label="Date Left" required field="dateLeft" error={ruleError}>
              <input
                type="date"
                value={dateLeft}
                min={joined || undefined}
                max={today}
                onChange={e => setDateLeft(e.target.value)}
                className={inputCls}
              />
            </Field>
          </FormValidationProvider>
          {error && <ErrorBanner>{error}</ErrorBanner>}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className={btnGhost}>Cancel</button>
            <button onClick={confirm} disabled={submitting} className="px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 disabled:opacity-50">
              {submitting ? 'Saving…' : 'Mark as Left'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReactivateModal = ({ staff, onClose, onDone }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await fetchJSON(`/api/qm/staff/${staff.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ dateLeft: null }),
      });
      onDone();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className={modalOverlayCls}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <ModalHeader title="Reactivate Staff" onClose={onClose} />
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            Reactivate <span className="font-semibold">{staff.name}</span>? The leaving
            date <span className="font-semibold">{formatDate(staff.dateLeft)}</span> will be cleared,
            and they will appear in active staff dropdowns again.
          </p>
          {error && <ErrorBanner>{error}</ErrorBanner>}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className={btnGhost}>Cancel</button>
            <button onClick={confirm} disabled={submitting} className={btnPrimary}>
              {submitting ? 'Saving…' : 'Reactivate'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QMStaff;
